(() => {
  if (window.IslanderAACalibration) return;
  const script = document.querySelector('script[data-islander-aa-calibration]');
  if (!script) return;
  const FEATURE = 'storefront_aa_202611', EXPERIMENT = 'storefront-aa-202611';
  const ASSIGNMENT = 'islander_aa_assignment', OPTOUT = 'islander_aa_optout', TTL = 30 * 86400000, REFRESH = 60000;
  const SDK_URL = 'https://cdn.jsdelivr.net/npm/@growthbook/growthbook@1.8.0/dist/bundles/index.min.js';
  const FEATURES_URL = 'https://cdn.growthbook.io/api/features/sdk-GgsXMkl1Dsn3TkFN';
  const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const url = new URL(window.location.href);
  const qaKey = key => /^(ab_force|workshop_variant|workshop_debug|preview_theme_id|new[-_]workshop[-_]page(?:_oct26)?$|gb[-_~]|growthbook[-_]|force[-_])/i.test(key) || key === FEATURE || key === EXPERIMENT;
  const inert = script.dataset.themeRole !== 'main' || window.Shopify?.designMode === true ||
    url.protocol !== 'https:' || !['islanderuk.com', 'www.islanderuk.com'].includes(url.hostname) ||
    [...url.searchParams.keys()].some(qaKey) || [...new URLSearchParams(url.hash.slice(1)).keys()].some(qaKey) ||
    window.navigator?.webdriver === true || /bot|crawl|spider|headless|lighthouse|prerender/i.test(window.navigator?.userAgent || '');
  let status = inert ? 'ineligible' : 'waiting_content', error = '', identity, selected, phase = null;
  let epoch = 0, measurementEpoch = 0, request, sdkPromise, poll, lastCheck = 0, attempted = false, wasAllowed = false, localOptout = false, scheduled = false, verified = false;
  const exposed = new Set(), measurements = new Map(), recordedEpochs = new Map(), publishing = new Map(), publishAttempts = new Map();
  const permission = () => {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      const denied = privacy?.currentVisitorConsent?.().analytics === 'no';
      return {allowed: !denied && privacy?.analyticsProcessingAllowed?.() === true, denied};
    } catch { return {allowed: false, denied: false}; }
  };
  const visible = () => {
    if (document.readyState === 'loading' || document.visibilityState !== 'visible' || document.prerendering === true ||
      document.documentElement.dataset.workshopRoutePending !== undefined || document.documentElement.dataset.workshopLoading !== undefined || window.IslanderWorkshopRoute?.active === true) return false;
    const main = document.querySelector('#MainContent');
    if (!main?.isConnected || main.closest('[hidden], [inert], [aria-hidden="true"]') || !main.getClientRects().length) return false;
    const style = window.getComputedStyle(main);
    if (style.display === 'none' || style.visibility !== 'visible' || style.opacity === '0') return false;
    return !main.checkVisibility || main.checkVisibility({checkOpacity: true, checkVisibilityCSS: true, contentVisibilityAuto: true});
  };
  const cookies = name => {
    try { return document.cookie.split(';').flatMap(value => {
      const split = value.indexOf('='); return split >= 0 && value.slice(0, split).trim() === name ? [value.slice(split + 1).trim()] : [];
    }); } catch { return []; }
  };
  const optedOut = () => localOptout || cookies(OPTOUT).includes('1');
  const writeCookie = (name, value, seconds) => {
    try {
      document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${seconds}; SameSite=Lax; Secure`;
      return seconds === 0 ? cookies(name).length === 0 : cookies(name).includes(encodeURIComponent(value));
    } catch { return false; }
  };
  const removeMeasurement = () => { try { window.IslanderExperimentAttribution?.remove(EXPERIMENT); } catch {} };
  const eraseIdentity = () => { identity = undefined; writeCookie(ASSIGNMENT, '', 0); };
  const readIdentity = () => {
    for (const raw of cookies(ASSIGNMENT)) {
      try {
        const value = JSON.parse(decodeURIComponent(raw));
        if (value?.v === 1 && UUID.test(value.id) && Number.isSafeInteger(value.expiresAt) && value.expiresAt > Date.now() && value.expiresAt <= Date.now() + TTL + 300000) return value;
      } catch {}
    }
  };
  const ensureIdentity = () => {
    let value = readIdentity();
    if (!value) {
      const id = window.crypto.randomUUID();
      if (!UUID.test(id)) throw Error('identity_unavailable');
      value = {v: 1, id, expiresAt: Date.now() + TTL};
      if (!writeCookie(ASSIGNMENT, JSON.stringify(value), Math.floor(TTL / 1000))) throw Error('storage_unavailable');
      const saved = readIdentity();
      if (saved?.id !== id || saved.expiresAt !== value.expiresAt) throw Error('storage_unavailable');
    }
    return value;
  };
  const clearSelection = nextStatus => { recordedEpochs.clear(); selected = undefined; phase = null; verified = false; status = nextStatus; removeMeasurement(); announce(); };
  const disable = () => {
    epoch++; measurementEpoch++; request?.controller.abort(); request = undefined;
    window.clearTimeout(poll); poll = undefined;
    eraseIdentity(); clearSelection('disabled'); attempted = false;
  };
  const loadSDK = () => {
    if (sdkPromise) return sdkPromise;
    sdkPromise = new Promise((resolve, reject) => {
      const node = document.createElement('script'); let finished = false;
      const finish = success => {
        if (finished) return; finished = true;
        window.clearTimeout(timer);
        node?.removeEventListener('load', loaded); node?.removeEventListener('error', failed);
        if (success && window.growthbook?.GrowthBook) resolve(window.growthbook.GrowthBook);
        else reject(Error('sdk_unavailable'));
      };
      const loaded = () => finish(true), failed = () => finish(false);
      const timer = window.setTimeout(failed, 2000);
      // Capture our pinned plain constructor, independently of the global auto SDK.
      node.src = SDK_URL; node.async = true; node.dataset.islanderAaSdk = '';
      if (script.nonce) node.nonce = script.nonce;
      node.addEventListener('load', loaded, {once: true}); node.addEventListener('error', failed, {once: true});
      document.head.append(node);
    }).catch(value => { sdkPromise = undefined; throw value; });
    return sdkPromise;
  };
  const fetchFeatures = controller => new Promise((resolve, reject) => {
    let finished = false;
    const finish = (value, failure) => {
      if (finished) return; finished = true; window.clearTimeout(timer);
      controller.signal.removeEventListener('abort', aborted);
      if (failure) reject(Error('configuration_unavailable')); else resolve(value);
    };
    const aborted = () => finish(undefined, true);
    const timer = window.setTimeout(() => { controller.abort(); finish(undefined, true); }, 2500);
    controller.signal.addEventListener('abort', aborted, {once: true});
    Promise.resolve().then(() => window.fetch(FEATURES_URL, {credentials: 'omit', signal: controller.signal})).then(response => {
      if (!response.ok) throw Error('configuration_unavailable'); return response.json();
    }).then(value => finish(value, false), () => finish(undefined, true));
  });
  const validRule = features => {
    const rules = features?.[FEATURE]?.rules;
    if (!Array.isArray(rules)) return;
    const matches = rules.filter(rule => rule?.key === EXPERIMENT);
    if (matches.length !== 1) return;
    const rule = matches[0];
    if (!['string', 'number'].includes(typeof rule.phase) || !/^[a-zA-Z0-9][a-zA-Z0-9_-]{0,39}$/.test(String(rule.phase)) ||
      Object.prototype.hasOwnProperty.call(rule, 'force') || rule.active === false || (rule.status !== undefined && rule.status !== 'running') ||
      JSON.stringify(rule.variations) !== '[true,true]' || JSON.stringify(rule.weights) !== '[0.5,0.5]' || rule.coverage !== 1 ||
      (rule.hashAttribute !== undefined && rule.hashAttribute !== 'id') ||
      (rule.ranges !== undefined && JSON.stringify(rule.ranges) !== '[[0,0.5],[0.5,1]]')) return;
    return rule;
  };
  const publish = () => {
    if (!selected || !verified || optedOut()) return;
    if (!permission().allowed) { status = 'assigned'; announce(); return; }
    const exposureKey = `${phase}:${selected.experimentResult.variationId}`;
    if (exposed.has(exposureKey) && recordedEpochs.get(exposureKey) === measurementEpoch) { status = 'assigned'; return; }
    if (publishing.has(exposureKey) || !visible()) return;
    const attribution = window.IslanderExperimentAttribution, result = selected.experimentResult;
    if (!attribution || typeof window.Shopify?.analytics?.publish !== 'function') { status = 'waiting_measurement'; return; }
    try {
      if (readIdentity()?.id !== identity?.id) { clearSelection('storage_unavailable'); return; }
      const currentEpoch = measurementEpoch;
      if (recordedEpochs.get(exposureKey) !== measurementEpoch) {
        // A shopper can opt back in and receive the other arm. Preserve that truthful
        // conflict instead of silently suppressing it or assuming the original arm.
        for (const previous of measurements.get(phase)?.values() || []) {
          if (previous.experimentResult.variationId === result.variationId) continue;
          if (attribution.record(previous, {phase, renderedValue: true}) !== true) return;
        }
        if (attribution.record(selected, {phase, renderedValue: true}) !== true || currentEpoch !== measurementEpoch || !permission().allowed) return;
        if (!measurements.has(phase)) measurements.set(phase, new Map());
        // Retain only the validated nonidentifying fields needed for a same-consent
        // mixed-arm record; never cache the SDK's hashValue/browser UUID here.
        measurements.get(phase).set(result.variationId, {
          source: selected.source, value: selected.value,
          experiment: {key: selected.experiment.key, active: selected.experiment.active, variations: [...selected.experiment.variations]},
          experimentResult: {inExperiment: result.inExperiment, hashUsed: result.hashUsed, variationId: result.variationId, value: result.value},
        });
        recordedEpochs.set(exposureKey, measurementEpoch);
      }
      status = 'assigned';
      if (exposed.has(exposureKey)) return;
      const exposedPhase = phase, count = publishAttempts.get(exposureKey) || 0;
      if (count >= 2) { status = 'publish_unavailable'; return; }
      const pending = {epoch: measurementEpoch, timer: undefined}; publishing.set(exposureKey, pending);
      publishAttempts.set(exposureKey, count + 1); status = 'publishing';
      const finish = (success, uncertain = false) => {
        if (publishing.get(exposureKey) !== pending) return;
        publishing.delete(exposureKey); window.clearTimeout(pending.timer);
        if (success) exposed.add(exposureKey);
        // Dispatch has no bearing on consent or attribution after its epoch has ended.
        if (pending.epoch !== measurementEpoch || phase !== exposedPhase || !permission().allowed || optedOut()) return;
        if (success) { status = 'assigned'; error = ''; announce(); return; }
        status = 'publish_unavailable'; error = 'publish_unavailable';
        if (uncertain) publishAttempts.set(exposureKey, 2);
        else if (publishAttempts.get(exposureKey) < 2) window.setTimeout(() => {
          if (pending.epoch === measurementEpoch && phase === exposedPhase && !optedOut()) publish();
        }, 1000);
      };
      // A timed-out dispatch is ambiguous: never retry it and risk a duplicate.
      pending.timer = window.setTimeout(() => finish(false, true), 2500);
      Promise.resolve().then(() => {
        if (pending.epoch !== measurementEpoch || !permission().allowed || optedOut() || !visible()) { finish(false); return false; }
        return window.Shopify.analytics.publish('islander:interaction', {
          event_name: 'experiment_viewed', experiment_id: EXPERIMENT, variation_id: String(result.variationId),
        });
      }).then(value => finish(value !== false), () => finish(false));
    } catch { clearSelection('measurement_unavailable'); }
  };
  const schedulePoll = () => {
    window.clearTimeout(poll);
    if (!inert && !optedOut()) poll = window.setTimeout(() => { poll = undefined; refresh(true); }, REFRESH);
  };
  const refresh = (force = false) => {
    if (inert) return;
    const access = permission();
    if (wasAllowed !== access.allowed) {
      measurementEpoch++; recordedEpochs.clear();
      if (!access.allowed) {
        measurements.clear(); exposed.clear(); publishAttempts.clear();
        for (const pending of publishing.values()) window.clearTimeout(pending.timer);
        publishing.clear(); removeMeasurement();
      }
      wasAllowed = access.allowed;
    }
    if (optedOut()) { disable(); return; }
    if (identity && readIdentity()?.id !== identity.id) {
      epoch++; request?.controller.abort(); request = undefined;
      identity = undefined; attempted = false; clearSelection('identity_changed');
    }
    if (!force && attempted && Date.now() - lastCheck < REFRESH && !selected) return;
    if (!visible()) { status = 'waiting_visible'; return; }
    if (request) return;
    if (!force && attempted && Date.now() - lastCheck < REFRESH) { publish(); return; }
    const controller = new window.AbortController(), token = {controller, epoch};
    request = token; attempted = true; verified = false; lastCheck = Date.now(); status = 'loading';
    const current = () => request === token && token.epoch === epoch && !optedOut();
    Promise.all([fetchFeatures(controller), loadSDK()]).then(([payload, GrowthBook]) => {
      if (!current()) return;
      const features = payload?.features;
      if (!features || typeof features !== 'object' || Array.isArray(features)) throw Error('configuration_unavailable');
      const attribution = window.IslanderExperimentAttribution;
      // This registry owns all active experiments, so never configure it with an A/A-only subset.
      if (!attribution || attribution.configure(features, {revision: payload.dateUpdated}) !== true) throw Error('measurement_unavailable');
      const rule = validRule(features);
      if (!rule) { eraseIdentity(); clearSelection('inactive'); return; }
      const publishedPhase = attribution.phaseFor(features, FEATURE, EXPERIMENT);
      if (publishedPhase !== String(rule.phase)) throw Error('phase_unavailable');
      identity = ensureIdentity();
      if (!identity || !current()) return;
      const gb = new GrowthBook({features, attributes: {id: identity.id, themeId: script.dataset.themeId}, trackingCallback: () => {}});
      let feature;
      try { feature = gb.evalFeature(FEATURE); } finally { gb.destroy(); }
      if (!current()) return;
      const experiment = feature?.experiment, result = feature?.experimentResult, index = result?.variationId;
      if (feature?.source !== 'experiment' || experiment?.key !== EXPERIMENT || experiment.active === false ||
        (experiment.status !== undefined && experiment.status !== 'running') || JSON.stringify(experiment.variations) !== '[true,true]' ||
        result?.inExperiment !== true || result.hashUsed !== true || ![0, 1].includes(index) || feature.value !== true ||
        (result.value !== undefined && result.value !== true) || (result.hashAttribute !== undefined && result.hashAttribute !== 'id') ||
        (result.hashValue !== undefined && result.hashValue !== identity.id)) { clearSelection('not_enrolled'); return; }
      selected = feature; phase = publishedPhase; verified = true; error = '';
      publish(); announce();
    }).catch(value => {
      if (!current()) return;
      error = value?.message === 'storage_unavailable' ? 'storage_unavailable' : 'experiment_unavailable';
      // Existing genuine exposure remains purchase history during a transient outage.
      // Only a known inactive/ineligible rule, identity change or withdrawal removes it.
      status = error;
    }).finally(() => {
      if (request === token) { request = undefined; schedulePoll(); }
    });
  };
  const resume = () => { refresh(); };
  const onConsent = () => { refresh(); announce(); };
  const onMutation = () => {
    if (selected && (!permission().allowed || exposed.has(`${phase}:${selected.experimentResult.variationId}`) || publishing.has(`${phase}:${selected.experimentResult.variationId}`))) return;
    if (scheduled) return; scheduled = true;
    Promise.resolve().then(() => { scheduled = false; resume(); });
  };
  const inspect = () => ({status, enabled: !inert && !optedOut(), arm: selected ? ['A', 'B'][selected.experimentResult.variationId] : null, phase,
    exposed: !!selected && exposed.has(`${phase}:${selected.experimentResult.variationId}`), analyticsAllowed: permission().allowed, error});
  let announced = '';
  function announce() {
    const detail = inspect(), signature = JSON.stringify(detail);
    if (signature === announced) return; announced = signature;
    document.dispatchEvent(new window.CustomEvent('islander:aa-calibration-change', {detail}));
  }
  window.IslanderAACalibration = {inspect, refresh: () => refresh(true), setEnabled: enabled => {
    if (inert || typeof enabled !== 'boolean') return false;
    const saved = writeCookie(OPTOUT, enabled ? '0' : '1', 365 * 86400);
    localOptout = !enabled && !saved;
    if (!enabled) disable(); else { attempted = false; refresh(true); }
    announce(); return saved;
  }};
  if (inert) return;
  for (const event of ['visitorConsentCollected', 'shopifyCustomerPrivacyApiLoaded']) document.addEventListener(event, onConsent);
  for (const event of ['DOMContentLoaded', 'visibilitychange', 'prerenderingchange', 'islander:workshop-experiment-ready', 'islander:experiment-attribution-ready']) document.addEventListener(event, resume);
  document.addEventListener('growthbookdata', () => refresh(true));
  window.addEventListener('focus', resume); window.addEventListener('pageshow', resume);
  if (window.MutationObserver) new window.MutationObserver(onMutation).observe(document.documentElement, {
    childList: true, subtree: true, attributes: true,
    attributeFilter: ['hidden', 'inert', 'aria-hidden', 'style', 'class', 'data-workshop-route-pending', 'data-workshop-loading'],
  });
  if (!window.Shopify?.customerPrivacy) {
    try { window.Shopify?.loadFeatures?.([{name: 'consent-tracking-api', version: '0.1'}], value => { if (!value) onConsent(); }); } catch {}
  }
  refresh();
})();
